import * as THREE from 'three'
import { FirstPersonControls as ThreeFirstPersonControls } from 'three/addons/controls/FirstPersonControls.js'
import { FIRST_PERSON_LOOK_SPEED, FIRST_PERSON_MOVEMENT_SPEED } from '@/shared/constants'
import { CameraMode } from '@/shared/types/core'
import { computeRollFreeUp, type NativeControlServices } from './NativeControlUtils'

/** FirstPersonController 构造参数 */
export interface FirstPersonControllerOptions {
  /** 控制器共享服务集合 */
  services: NativeControlServices
}

const THREE_FIRST_PERSON_LOOK_SPEED_MULTIPLIER = 10
const CONTROL_UP = new THREE.Vector3(0, 1, 0)
const ENGINE_TO_CONTROL_ROTATION = new THREE.Quaternion().setFromAxisAngle(
  new THREE.Vector3(1, 0, 0),
  -Math.PI / 2
)
const CONTROL_TO_ENGINE_ROTATION = ENGINE_TO_CONTROL_ROTATION.clone().invert()
const FIRST_PERSON_ACTIVATION_TRANSITION_MS = 180
const UP_ALIGNMENT_EPSILON = 1e-3

/** First-person mode powered by Three's official FirstPersonControls. */
export class FirstPersonController extends ThreeFirstPersonControls {
  private readonly services: NativeControlServices
  private readonly controlCamera: THREE.PerspectiveCamera
  private activationToken = 0
  private isActivating = false

  constructor(options: FirstPersonControllerOptions) {
    const camera = new THREE.PerspectiveCamera()
    super(camera)

    this.services = options.services
    this.controlCamera = camera
    this.enabled = false
    this.movementSpeed = FIRST_PERSON_MOVEMENT_SPEED
    this.lookSpeed = FIRST_PERSON_LOOK_SPEED * THREE_FIRST_PERSON_LOOK_SPEED_MULTIPLIER
    this.lookVertical = true
  }

  /**
   * 连接 DOM 元素并进入第一人称模式。
   *
   * @param element - 承载交互事件的 HTML 元素
   */
  override connect(element: HTMLElement): void {
    if (this.services.rig.mode !== CameraMode.Perspective) {
      const state = this.services.rig.getState()
      this.services.rig.setState({
        ...state,
        mode: CameraMode.Perspective,
      })
    }

    this.syncProxyFromRig()
    super.connect(element)
    this.controlCamera.updateMatrixWorld(true)
    void this.beginActivationTransition()
  }

  /**
   * 每帧更新控制器。
   *
   * @param delta - 自上一帧的时间间隔，单位秒
   */
  override update(delta: number): void {
    if (!this.enabled) return

    this.syncProxyFromRig()
    if (this.isActivating) {
      this.controlCamera.updateMatrixWorld(true)
      return
    }
    super.update(delta)

    const direction = new THREE.Vector3()
    this.controlCamera.getWorldDirection(direction)
    this.controlCamera.updateMatrixWorld(true)
    this.syncRigFromProxy(direction)
  }

  /** 释放控制器资源并中止激活过渡 */
  override dispose(): void {
    this.activationToken += 1
    this.isActivating = false
    if (this.domElement) {
      super.dispose()
    }
  }

  private syncProxyFromRig(): void {
    const state = this.services.rig.getState()
    const controlPosition = this.engineToControl(state.position)
    const controlTarget = this.engineToControl(state.target)

    this.controlCamera.position.copy(controlPosition)
    this.controlCamera.up.copy(CONTROL_UP)
    this.controlCamera.fov = state.fov
    this.controlCamera.near = this.services.rig.near
    this.controlCamera.far = this.services.rig.far
    this.controlCamera.lookAt(controlTarget)
    this.controlCamera.updateProjectionMatrix()
    this.controlCamera.updateMatrixWorld(true)
    this.lookAt(controlTarget)
  }

  private syncRigFromProxy(controlDirection: THREE.Vector3): void {
    const previous = this.services.rig.getState()
    const distance = Math.max(previous.position.distanceTo(previous.target), 1)
    const enginePosition = this.controlToEngine(this.controlCamera.position)
    const engineDirection = this.controlToEngineDirection(controlDirection)
    const target = enginePosition.clone().addScaledVector(engineDirection, distance)
    const up = computeRollFreeUp(engineDirection)

    this.services.rig.setState({
      ...previous,
      position: enginePosition,
      target,
      up,
      fov: this.controlCamera.fov,
      mode: CameraMode.Perspective,
    })
  }

  private async beginActivationTransition(): Promise<void> {
    const state = this.services.rig.getState()
    const forward = state.target.clone().sub(state.position)
    if (forward.lengthSq() < 1e-8) {
      this.isActivating = false
      return
    }

    const desiredUp = computeRollFreeUp(forward)
    if (desiredUp.angleTo(state.up) <= UP_ALIGNMENT_EPSILON) {
      this.isActivating = false
      return
    }

    const token = ++this.activationToken
    this.isActivating = true

    await this.services.rig.animateTo(
      {
        up: desiredUp,
        mode: CameraMode.Perspective,
      },
      FIRST_PERSON_ACTIVATION_TRANSITION_MS
    )

    if (this.activationToken !== token) {
      return
    }

    this.isActivating = false
    this.syncProxyFromRig()
  }

  private engineToControl(vector: THREE.Vector3): THREE.Vector3 {
    return vector.clone().applyQuaternion(ENGINE_TO_CONTROL_ROTATION)
  }

  private controlToEngine(vector: THREE.Vector3): THREE.Vector3 {
    return vector.clone().applyQuaternion(CONTROL_TO_ENGINE_ROTATION)
  }

  private controlToEngineDirection(vector: THREE.Vector3): THREE.Vector3 {
    return this.controlToEngine(vector).normalize()
  }
}
